import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ScrollView,
  Alert,
} from 'react-native';
import Text from '../../common/Text';
import { useTheme } from '../../../hooks/useTheme';
import { useAnalytics, exportAnalyticsCSV } from '../../../hooks/useAdminData';
import { Ionicons } from '@expo/vector-icons';
import { palette } from '../../../utils/palette';

const AnalyticsView: React.FC = () => {
  const { colors } = useTheme();
  const [period, setPeriod] = useState<string>('month');
  const {
    data: analytics,
    isLoading,
    error,
  } = useAnalytics(period, {
    enabled: true,
  });

  const handleExport = async (
    dataType: 'users' | 'moodboards' | 'palettes' | 'feedback'
  ) => {
    try {
      if (Platform.OS === 'web') {
        await exportAnalyticsCSV(dataType);
        Alert.alert('Success', `${dataType} data exported successfully`);
      } else {
        Alert.alert('Info', 'CSV export is only available on web');
      }
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to export data');
    }
  };

  if (isLoading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Text style={[styles.loading, { color: colors.secondary }]}>
          Loading analytics...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Text
          style={[
            styles.error,
            { color: colors.errorText || palette.brightMatteRed },
          ]}
        >
          Error loading analytics: {(error as Error).message}
        </Text>
      </View>
    );
  }

  if (!analytics) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Text style={[styles.empty, { color: colors.secondary }]}>
          No analytics data available
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      {/* Period Selector and Export Options */}
      <View
        style={[styles.periodSelector, { borderBottomColor: colors.border }]}
      >
        <View style={styles.periodAndExportRow}>
          <View style={styles.periodSection}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              Time Period:
            </Text>
            <View style={styles.periodButtons}>
              {['day', 'week', 'month', 'year'].map((p) => (
                <TouchableOpacity
                  key={p}
                  style={[
                    styles.periodButton,
                    {
                      backgroundColor:
                        period === p ? colors.primary : colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                  onPress={() => setPeriod(p)}
                >
                  <Text
                    style={[
                      styles.periodButtonText,
                      { color: period === p ? palette.white : colors.text },
                    ]}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Export Options */}
          {Platform.OS === 'web' && (
            <View style={styles.exportSection}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                Export Data (CSV)
              </Text>
              <View style={styles.exportButtons}>
                <TouchableOpacity
                  style={[
                    styles.exportButton,
                    { backgroundColor: palette.brightBlue },
                  ]}
                  onPress={() => handleExport('users')}
                >
                  <Ionicons name="download" size={14} color={palette.white} />
                  <Text
                    style={[styles.exportButtonText, { color: palette.white }]}
                  >
                    Users
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.exportButton,
                    { backgroundColor: palette.cyanLimeGreen },
                  ]}
                  onPress={() => handleExport('moodboards')}
                >
                  <Ionicons name="download" size={14} color={palette.white} />
                  <Text
                    style={[styles.exportButtonText, { color: palette.white }]}
                  >
                    Moodboards
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.exportButton,
                    { backgroundColor: palette.lightOrange },
                  ]}
                  onPress={() => handleExport('palettes')}
                >
                  <Ionicons name="download" size={14} color={palette.white} />
                  <Text
                    style={[styles.exportButtonText, { color: palette.white }]}
                  >
                    Palettes
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.exportButton,
                    { backgroundColor: palette.brightViolet },
                  ]}
                  onPress={() => handleExport('feedback')}
                >
                  <Ionicons name="download" size={14} color={palette.white} />
                  <Text
                    style={[styles.exportButtonText, { color: palette.white }]}
                  >
                    Feedback
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* System Stats */}
      <View
        style={[
          styles.section,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          System Usage
        </Text>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.system_stats?.total_users || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Total Users
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.system_stats?.active_users || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Active Users
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.system_stats?.total_color_profiles || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Colour Profiles
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.system_stats?.total_moodboards || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Moodboards
            </Text>
          </View>
        </View>
      </View>

      {/* Active Users */}
      <View
        style={[
          styles.section,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          Active Users
        </Text>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: palette.cyanLimeGreen }]}>
              {analytics.active_users?.daily || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Daily Active
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: palette.cyanLimeGreen }]}>
              {analytics.active_users?.weekly || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Weekly Active
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: palette.cyanLimeGreen }]}>
              {analytics.active_users?.monthly || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Monthly Active
            </Text>
          </View>
        </View>
      </View>

      {/* Engagement Metrics */}
      <View
        style={[
          styles.section,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          Engagement Metrics
        </Text>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.engagement?.total_likes || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Total Likes
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.engagement?.total_views || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Total Views
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.engagement?.total_shares || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Total Shares
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statValue, { color: colors.primary }]}>
              {analytics.engagement?.avg_likes_per_moodboard || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Avg Likes/Board
            </Text>
          </View>
        </View>
      </View>

      {/* Top 5 Popular Moodboards */}
      <View
        style={[
          styles.section,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          Top 5 Popular Moodboards
        </Text>
        {analytics.popular_moodboards &&
        analytics.popular_moodboards.length > 0 ? (
          <View style={styles.list}>
            {analytics.popular_moodboards.map((mb: any, index: number) => (
              <View
                key={mb.id}
                style={[styles.listItem, { borderBottomColor: colors.border }]}
              >
                <View style={styles.listItemContent}>
                  <Text
                    style={[styles.listItemRank, { color: colors.primary }]}
                  >
                    #{index + 1}
                  </Text>
                  <View style={styles.listItemInfo}>
                    <Text
                      style={[styles.listItemTitle, { color: colors.text }]}
                    >
                      {mb.name}
                    </Text>
                    <Text
                      style={[
                        styles.listItemSubtitle,
                        { color: colors.secondary },
                      ]}
                    >
                      Likes: {mb.likes_count} | Views: {mb.views_count} |
                      Shares: {mb.shares_count || 0}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        ) : (
          <Text style={[styles.empty, { color: colors.secondary }]}>
            No popular moodboards
          </Text>
        )}
      </View>

      {/* Top 5 Popular Palettes */}
      <View
        style={[
          styles.section,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          Top 5 Popular Colour Palettes
        </Text>
        {analytics.popular_palettes && analytics.popular_palettes.length > 0 ? (
          <View style={styles.list}>
            {analytics.popular_palettes.map((palette: any, index: number) => (
              <View
                key={palette.id}
                style={[styles.listItem, { borderBottomColor: colors.border }]}
              >
                <View style={styles.listItemContent}>
                  <Text
                    style={[styles.listItemRank, { color: colors.primary }]}
                  >
                    #{index + 1}
                  </Text>
                  <View style={styles.listItemInfo}>
                    <Text
                      style={[styles.listItemTitle, { color: colors.text }]}
                    >
                      {palette.name}
                    </Text>
                    <Text
                      style={[
                        styles.listItemSubtitle,
                        { color: colors.secondary },
                      ]}
                    >
                      User ID: {palette.user_id} | Created:{' '}
                      {new Date(palette.created_at).toLocaleString()}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        ) : (
          <Text style={[styles.empty, { color: colors.secondary }]}>
            No popular palettes
          </Text>
        )}
      </View>

      {/* User Growth Chart (Simple text representation) */}
      {analytics.user_growth && analytics.user_growth.length > 0 && (
        <View
          style={[
            styles.section,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            User Growth Trend
          </Text>
          <View style={styles.chartContainer}>
            <Text style={[styles.chartInfo, { color: colors.secondary }]}>
              Total sign-ups in selected period:{' '}
              {analytics.user_growth.reduce(
                (sum: number, item: any) => sum + item.count,
                0
              )}
            </Text>
            {Platform.OS === 'web' && (
              <div
                style={{
                  marginTop: 16,
                  padding: 16,
                  backgroundColor: colors.background,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '4px',
                    height: '200px',
                  }}
                >
                  {analytics.user_growth.map((item: any, index: number) => {
                    const maxCount = Math.max(
                      ...analytics.user_growth.map((i: any) => i.count)
                    );
                    const height =
                      maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    return (
                      <div
                        key={index}
                        style={{
                          flex: 1,
                          backgroundColor: colors.primary,
                          height: `${height}%`,
                          minHeight: '4px',
                          borderRadius: '4px 4px 0 0',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                        title={`${item.period}: ${item.count} users`}
                      >
                        <span
                          style={{
                            fontSize: '10px',
                            color: colors.text,
                            marginBottom: '4px',
                          }}
                        >
                          {item.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </View>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  loading: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
  },
  error: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
  },
  empty: {
    textAlign: 'center',
    marginTop: 10,
    fontSize: 12,
  },
  periodSelector: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  periodAndExportRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    ...(Platform.OS === 'web'
      ? {}
      : {
          flexDirection: 'column',
        }),
  },
  periodSection: {
    flex: 1,
  },
  exportSection: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? {
          minWidth: 300,
        }
      : {}),
  },
  periodButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  periodButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
  },
  periodButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  section: {
    marginBottom: 20,
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: 120,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    textAlign: 'center',
  },
  list: {
    marginTop: 8,
  },
  listItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  listItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  listItemRank: {
    fontSize: 18,
    fontWeight: '700',
    marginRight: 12,
    width: 30,
  },
  listItemInfo: {
    flex: 1,
  },
  listItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  listItemSubtitle: {
    fontSize: 12,
  },
  chartContainer: {
    marginTop: 8,
  },
  chartInfo: {
    fontSize: 12,
    marginBottom: 8,
  },
  exportButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 4,
  },
  exportButtonText: {
    fontSize: 11,
    fontWeight: '600',
  },
});

export default AnalyticsView;
